import { Vector2, Vector3, Vector4 } from "three";
import { cylinderDistance, cylinderNormal } from "./cylinder-surface";
import {
  depthPosition,
  selectDepthSurface,
  type DepthSnapshot,
} from "./depth-surface";
import type { SurfaceRequest } from "./surface-wand-context";
import type { SurfaceSelection } from "./surface-selection-pass";
import { WorldSurface } from "./world-surface";
import { cylinderAxisFromNormals } from "./cylinder-fit";
import { fitCircleRobust } from "./circle-fit";
import { fitCylinderInline, type CylinderFitter } from "./cylinder-fitting";

const PATCH_RADIUS = 48;
const MAX_PATCH_RADIUS = 128;
const MAX_SAMPLES = 1536;
const MIN_SAMPLES = 24;
const NORMAL_ALIGNMENT = Math.cos(Math.PI / 9);
const LIMITS = {
  normalLengthSquared: 0.9,
  residualPercentile: 0.8,
  minimumResidual: 0.0005,
  maximumResidual: 0.025,
  residualRadiusRatio: 0.05,
  residualPixelRadiusRatio: 0.1,
  minimumTolerance: 0.001,
  maximumTolerance: 0.05,
  toleranceRadiusRatio: 0.02,
  maximumToleranceRadiusRatio: 0.2,
};

/**
 * @param snapshot Bounded depth capture.
 * @param request Clicked seed, shape and optional retained cylinder.
 * @param seed Captured seed pixel.
 * @param origin Captured world-space seed.
 * @param cancelled Whether this request is obsolete.
 * @param fitCylinder Joint geometric fitting adapter; the live tool supplies a worker.
 * @returns Fast connected cylinder preview on the fixed periodic world grid.
 */
export async function fitDepthCylinder(
  snapshot: DepthSnapshot,
  request: SurfaceRequest,
  seed: number,
  origin: Vector3,
  cancelled: () => boolean,
  fitCylinder: CylinderFitter = fitCylinderInline,
): Promise<SurfaceSelection | undefined> {
  const column = seed % snapshot.width;
  const row = Math.floor(seed / snapshot.width);
  const maximum = Math.min(
    MAX_PATCH_RADIUS,
    Math.max(
      column,
      snapshot.width - 1 - column,
      row,
      snapshot.height - 1 - row,
    ),
  );
  for (
    let radius = Math.min(PATCH_RADIUS, maximum);
    ;
    radius = Math.min(radius * 2, maximum)
  ) {
    if (cancelled()) return;
    const selection = await fitDepthCylinderPatch(
      snapshot,
      request,
      seed,
      origin,
      cancelled,
      fitCylinder,
      radius,
    );
    if (selection || radius === maximum) return selection;
  }
}

async function fitDepthCylinderPatch(
  snapshot: DepthSnapshot,
  request: SurfaceRequest,
  seed: number,
  origin: Vector3,
  cancelled: () => boolean,
  fitCylinder: CylinderFitter,
  patchRadius: number,
): Promise<SurfaceSelection | undefined> {
  const { width, height, depth } = snapshot;
  const column = seed % width;
  const row = Math.floor(seed / width);
  const neighbor = new Vector3();
  const horizontal = new Vector3();
  const vertical = new Vector3();
  const forward = new Vector3();
  const backward = new Vector3();
  function valid(horizontal: number, vertical: number): boolean {
    const value = depth[vertical * width + horizontal];
    return (
      horizontal >= 0 &&
      vertical >= 0 &&
      horizontal < width &&
      vertical < height &&
      Number.isFinite(value) &&
      value > 0 &&
      value < 1
    );
  }
  const spacings: number[] = [];
  for (const [offsetColumn, offsetRow] of [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ]) {
    if (valid(column + offsetColumn, row + offsetRow)) {
      spacings.push(
        depthPosition(
          snapshot,
          column + offsetColumn,
          row + offsetRow,
        ).distanceTo(origin),
      );
    }
  }
  spacings.sort((left, right) => left - right);
  if (spacings.length < 2 || cancelled()) return;
  const maxStep = Math.max(0.01, spacings[1] * 4);
  function direction(
    point: Vector3,
    column: number,
    row: number,
    horizontal: number,
    vertical: number,
    target: Vector3,
  ): boolean {
    const hasForward =
      valid(column + horizontal, row + vertical) &&
      depthPosition(snapshot, column + horizontal, row + vertical, forward)
        .sub(point)
        .length() <
        maxStep * 2;
    const hasBackward =
      valid(column - horizontal, row - vertical) &&
      backward
        .subVectors(
          point,
          depthPosition(
            snapshot,
            column - horizontal,
            row - vertical,
            neighbor,
          ),
        )
        .length() <
        maxStep * 2;
    if (!hasForward && !hasBackward) return false;
    target.copy(hasForward ? forward : backward);
    if (hasForward && hasBackward) target.add(backward);
    return target.lengthSq() > 0;
  }
  const queue = [seed];
  const visited = new Set([seed]);
  const samples: Array<{ point: Vector3; normal: Vector3 }> = [];
  for (const index of queue) {
    if (cancelled()) return;
    const currentColumn = index % width;
    const currentRow = Math.floor(index / width);
    const point = depthPosition(snapshot, currentColumn, currentRow);
    if (
      (direction(point, currentColumn, currentRow, 1, 0, horizontal) ||
        direction(point, currentColumn, currentRow, 2, 0, horizontal)) &&
      (direction(point, currentColumn, currentRow, 0, 1, vertical) ||
        direction(point, currentColumn, currentRow, 0, 2, vertical))
    ) {
      const normal = new Vector3()
        .crossVectors(horizontal, vertical)
        .normalize();
      if (normal.lengthSq() > LIMITS.normalLengthSquared) {
        samples.push({ point, normal });
      }
    }
    for (const [offsetColumn, offsetRow] of [
      [-1, 0],
      [1, 0],
      [0, -1],
      [0, 1],
    ]) {
      const nextColumn = currentColumn + offsetColumn;
      const nextRow = currentRow + offsetRow;
      const next = nextRow * width + nextColumn;
      if (
        visited.has(next) ||
        Math.abs(nextColumn - column) > patchRadius ||
        Math.abs(nextRow - row) > patchRadius ||
        !valid(nextColumn, nextRow)
      ) {
        continue;
      }
      visited.add(next);
      if (
        depthPosition(snapshot, nextColumn, nextRow, neighbor).distanceTo(
          point,
        ) < maxStep
      ) {
        queue.push(next);
      }
    }
  }
  if (samples.length < MIN_SAMPLES || cancelled()) return;
  const stride = Math.max(1, Math.ceil(samples.length / MAX_SAMPLES));
  const bounded = samples.filter((_, index) => index % stride === 0);
  const previous = request.addTo;
  let cylinder = previous?.cylinder;
  let tolerance = previous?.tolerance ?? 0;
  if (
    previous &&
    (!cylinder ||
      previous.cloudId !== request.cloudId ||
      previous.cameraId !== request.cameraId)
  ) {
    return;
  }
  if (!cylinder) {
    const axis = cylinderAxisFromNormals(bounded.map(({ normal }) => normal));
    if (!axis || cancelled()) return;
    const radial = new Vector3(1, 0, 0).projectOnPlane(axis);
    if (radial.lengthSq() < LIMITS.residualPixelRadiusRatio) {
      radial.set(0, 0, 1).projectOnPlane(axis);
    }
    radial.normalize();
    const tangent = new Vector3().crossVectors(axis, radial);
    const center = bounded
      .reduce((sum, sample) => sum.add(sample.point), new Vector3())
      .divideScalar(bounded.length);
    const section = fitCircleRobust(
      bounded.map(({ point }) => {
        const offset = point.clone().sub(center);
        return new Vector2(offset.dot(radial), offset.dot(tangent));
      }),
      spacings[1],
      LIMITS.minimumResidual,
    );
    if (!section) return;
    const fitted = await fitCylinder(
      bounded.map(({ point }) => point),
      {
        axis,
        radial,
        center: center
          .addScaledVector(radial, section.center.x)
          .addScaledVector(tangent, section.center.y),
        radius: section.radius,
      },
      spacings[1],
      cancelled,
      true,
    );
    if (!fitted.accepted || cancelled()) return;
    const fittedCylinder = fitted.cylinder;
    cylinder = fittedCylinder;
    const residuals = bounded
      .map(({ point }) => Math.abs(cylinderDistance(fittedCylinder, point)))
      .sort((left, right) => left - right);
    const residual =
      residuals[Math.floor(residuals.length * LIMITS.residualPercentile)];
    if (
      residual >
      Math.max(
        LIMITS.minimumResidual,
        Math.min(
          LIMITS.maximumResidual,
          cylinder.radius * LIMITS.residualRadiusRatio,
        ),
        Math.min(
          LIMITS.maximumResidual,
          spacings[1],
          cylinder.radius * LIMITS.residualPixelRadiusRatio,
        ),
      )
    ) {
      return;
    }
    tolerance = Math.min(
      LIMITS.maximumTolerance,
      cylinder.radius * LIMITS.maximumToleranceRadiusRatio,
      Math.max(
        LIMITS.minimumTolerance,
        residual * 3,
        cylinder.radius * LIMITS.toleranceRadiusRatio,
      ),
    );
  }
  if (
    Math.abs(cylinderDistance(cylinder, origin)) > tolerance ||
    Math.abs(cylinderDistance(cylinder, request.point)) > tolerance
  ) {
    return;
  }
  const expected = cylinderNormal(cylinder, origin);
  if (Math.abs(samples[0].normal.dot(expected)) < NORMAL_ALIGNMENT) return;
  const region = await selectDepthSurface(
    snapshot,
    seed,
    cylinder,
    tolerance,
    cancelled,
  );
  if (!region || cancelled()) return;
  const preview: SurfaceSelection = {
    ...snapshot,
    cylinder,
    mask: region.mask,
    viewProjection: snapshot.inverseViewProjection.clone().invert(),
    origin: previous?.origin ?? origin,
    plane: new Vector4(
      expected.x,
      expected.y,
      expected.z,
      -expected.dot(origin),
    ),
    tolerance,
    positions: new Float32Array(),
    outline: new Float32Array(),
    cloudId: request.cloudId,
    cameraId: request.cameraId,
  };
  const grid = new WorldSurface(previous ?? preview);
  await grid.initialize(cancelled);
  if (previous) await grid.addSelection(preview, cancelled);
  if (cancelled() || grid.limited || !grid.seedTiles.size) return;
  const selection = grid.selection("growing");
  return selection.positions.length ? selection : undefined;
}
